import React, { useState } from 'react';
import { ShieldCheck, HelpCircle, PauseCircle, ChevronDown } from 'lucide-react';

const STYLES = {
  audit_specific: {
    Icon: ShieldCheck,
    chip: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    icon: 'text-emerald-700',
  },
  legacy_unverified: {
    Icon: HelpCircle,
    chip: 'border-amber-300 bg-amber-50 text-amber-900',
    icon: 'text-amber-700',
  },
  under_review: {
    Icon: PauseCircle,
    chip: 'border-red-300 bg-red-50 text-red-900',
    icon: 'text-red-700',
  },
};

const formatStamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const AnalysisProvenanceBadge = ({ badge, compact = false }) => {
  const [open, setOpen] = useState(false);
  if (!badge?.code) return null;

  const style = STYLES[badge.code] || STYLES.legacy_unverified;
  const { Icon } = style;
  const generated = formatStamp(badge.generatedAt);

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${style.chip}`}
        title={badge.description}
        data-testid={`analysis-provenance-chip-${badge.code}`}
      >
        <Icon className={`h-3 w-3 ${style.icon}`} />
        {badge.label}
      </span>
    );
  }

  return (
    <div
      className={`rounded-lg border px-3 py-2 ${style.chip}`}
      data-testid={`analysis-provenance-${badge.code}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 text-left"
        data-testid="analysis-provenance-toggle"
      >
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} />
        <span className="flex-1">
          <span className="block text-sm font-semibold" data-testid="analysis-provenance-label">
            {badge.label}
          </span>
          <span className="block text-xs opacity-90">{badge.description}</span>
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <dl className="mt-2 grid gap-1 border-t border-current/20 pt-2 text-xs"
            data-testid="analysis-provenance-details">
          {generated && (
            <div className="flex justify-between gap-3">
              <dt className="opacity-80">Analysed</dt>
              <dd className="font-medium" data-testid="analysis-provenance-generated-at">{generated}</dd>
            </div>
          )}
          {badge.methodLabel && (
            <div className="flex justify-between gap-3">
              <dt className="opacity-80">Method</dt>
              <dd className="font-medium text-right" data-testid="analysis-provenance-method">
                {badge.methodLabel}
              </dd>
            </div>
          )}
          {badge.analysisVersion && (
            <div className="flex justify-between gap-3">
              <dt className="opacity-80">Analysis version</dt>
              <dd className="font-medium">{badge.analysisVersion}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="opacity-80">Regenerated</dt>
            <dd className="font-medium" data-testid="analysis-provenance-regenerated">
              {badge.regenerated ? `Yes (${badge.regenerationCount || 1})` : 'No'}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="opacity-80">Review status</dt>
            <dd className="font-medium" data-testid="analysis-provenance-review-status">
              {badge.underReview ? 'Under review' : (badge.reviewStatus || 'not_reviewed').replace(/_/g, ' ')}
            </dd>
          </div>
          {badge.note && (
            <p className="pt-1 font-medium" data-testid="analysis-provenance-note">{badge.note}</p>
          )}
        </dl>
      )}
    </div>
  );
};

export default AnalysisProvenanceBadge;
